/**
 * Money is stored as integers at 1/10,000 (see @hisense/shared money.ts).
 * Display only here. Totals render at 2dp; unit prices keep the decimals the
 * value actually carries, up to 4 — a button really does cost $0.0122, and
 * showing $0.01 would be a lie the ledger can't afford. All amounts render in
 * IBM Plex Mono / tabular numerals — the instrument-readout signature.
 */
import { MONEY_SCALE, MONEY_DP } from "@hisense/shared";

const SYMBOL: Record<string, string> = {
  USD: "$",
  HKD: "HK$",
  CNY: "¥",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
};

/**
 * Ký hiệu của một đồng tiền, và nếu không biết thì in CHÍNH MÃ đồng tiền đó.
 *
 * Trước đây bảng chỉ có USD/HKD/CNY còn lại trả chuỗi rỗng, nên EUR/GBP/JPY —
 * ba đồng đang có trong danh sách chọn của chính hệ thống — hiện ra một con số
 * trần không biết là tiền gì. Trên chứng từ tiền bạc, một con số không đơn vị
 * tệ hơn một ký hiệu xấu.
 *
 * ¥ dùng cho cả CNY lẫn JPY là chuyện có thật của ký hiệu này; chỗ nào cần
 * phân biệt thì hiện kèm mã (xem formatMoneyWithCode).
 */
function symbolFor(currency: string): string {
  return SYMBOL[currency] ?? `${currency} `;
}

export function formatMoney(
  minor: number,
  currency = "USD",
  opts: { sign?: boolean; symbol?: boolean } = {},
): string {
  const { sign = false, symbol = true } = opts;
  const negative = minor < 0;
  const abs = Math.abs(minor) / MONEY_SCALE;
  const body = abs.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const sym = symbol ? symbolFor(currency) : "";
  const prefix = negative ? "−" : sign ? "+" : "";
  return `${prefix}${sym}${body}`;
}

/**
 * Unit prices render at a FIXED 4 decimals — the same resolution the storage
 * scale holds.
 *
 * Not merely for tidiness: in a tabular-numeral column, rows with different
 * decimal counts put the decimal point in different places, so the eye can no
 * longer scan a price column vertically. Padding 0.30 to 0.3000 costs two
 * characters and buys an aligned column. It also matches the legacy app, which
 * printed prices with toFixed(4).
 *
 * Document TOTALS stay at 2dp via formatMoney — an invoice total reads
 * $236,342.62, never $236,342.6200.
 */
export function formatUnitPrice(minor: number, currency = "USD", symbol = true): string {
  const negative = minor < 0;
  const abs = Math.abs(minor) / MONEY_SCALE;
  const body = abs.toLocaleString("en-US", {
    minimumFractionDigits: MONEY_DP,
    maximumFractionDigits: MONEY_DP,
  });
  const sym = symbol ? symbolFor(currency) : "";
  return `${negative ? "−" : ""}${sym}${body}`;
}

/** Plain edit-field form of a unit price: no symbol, no grouping, no trailing noise. */
export function unitPriceToInput(minor: number): string {
  const v = minor / MONEY_SCALE;
  return String(Number(v.toFixed(MONEY_DP)));
}

/** Compact form for KPI tiles, e.g. $1.24M, $86.4k. */
export function formatCompactMoney(minor: number, currency = "USD"): string {
  const v = minor / MONEY_SCALE;
  const sym = symbolFor(currency);
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${sym}${(v / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sym}${(v / 1_000).toFixed(1)}k`;
  return `${sym}${v.toFixed(0)}`;
}

export function formatQty(n: number): string {
  return n.toLocaleString("en-US");
}

/**
 * Số tiền dạng đơn vị lớn, KHÔNG có ký hiệu tiền tệ và KHÔNG phân cách nghìn —
 * dùng cho file xuất ra.
 *
 * Excel phải nhận được một con số để tính toán. `formatMoney` chèn "$" và dấu
 * phẩy phân cách nghìn, và ô đó vào Excel là CHỮ, không cộng được — người dùng
 * xuất bảng ra rồi không tính tổng được là mất luôn lý do xuất.
 *
 * Dấu chấm thập phân, không phải dấu phẩy: dấu phẩy sẽ tách ô trong CSV.
 */
export function majorUnits(minor: number | null | undefined, dp = 2): string {
  if (minor === null || minor === undefined) return "";
  return (minor / MONEY_SCALE).toFixed(dp);
}
